import { PUBLIC_PATHS } from 'src/constants';
import { defaultLocale, locales } from 'src/i18n/config';

// Pure, client-safe path helpers. Kept free of `next/headers` (and any other
// server-only imports) so they can be used from client components as well as
// the server. urlUtils.ts re-exports these for server-side consumers.

/**
 * Strips basePath and locale prefix from a raw pathname, returning just the
 * route segment (no query string). Useful for matching against PUBLIC_PATHS.
 */
export function getPathnameOnly(rawPathname: string): string {
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH ?? '';
  const pathAfterBase = basePath && rawPathname.startsWith(basePath) ? rawPathname.slice(basePath.length) : rawPathname;
  const locale =
    (locales as readonly string[]).find((l) => pathAfterBase.startsWith(`/${l}/`) || pathAfterBase === `/${l}`) ??
    defaultLocale;
  const pathWithoutLocale =
    locale !== defaultLocale && pathAfterBase.startsWith(`/${locale}`)
      ? pathAfterBase.slice(locale.length + 1)
      : pathAfterBase;
  return pathWithoutLocale.split('?')[0];
}

/**
 * Validate that a value is a safe, same-origin relative path so we don't
 * forward users to an attacker-controlled destination after login.
 */
export function isSafeRelativePath(value: unknown): value is string {
  if (typeof value !== 'string' || value.length === 0) return false;
  // Must start with a single forward slash (no protocol-relative // or backslash tricks).
  if (!value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) return false;
  // No scheme or host injection.
  if (value.includes(':')) return false;
  return true;
}

export function isPublicPath(pathnameOnly: string): boolean {
  return (PUBLIC_PATHS as readonly string[]).some((p) => pathnameOnly === p || pathnameOnly.startsWith(p + '/'));
}
